import {TableModel} from 'notebook/tables/store/models/TableModel';
import {createDebuggableSelector} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {
  createTableEditableStoreStructuredSelector,
  TableEditableStore,
} from 'notebook/tables/store/TableStore';

/**
 * The table-level values a cell renders with. These are identical for every cell, but they are
 * relayed through the cell model so that a cell has everything it needs to render in one place
 * rather than reaching back into the {@link TableModel} for each value individually.
 */
export interface CellTableModel {
  /** The id of the note file the table belongs to, used by cell attachments and links. */
  fileId: string;
  /** The namespace the table's columns are addressed under in formulas. */
  tableNamespace: string;
  /** Whether selector columns are enabled for the table. */
  selectorsEnabled: boolean;
  /** Whether the table is in a fill-in only entry, which blocks editing selectors. */
  isProtected: boolean;
  /** The height of the header row, which the cell editor positions itself against. */
  headerRowHeight?: number;
  /** Opens the selector management modal from a cell's selector dropdown. */
  openManageSelectorsModal: (initialSelectorId: string) => void;
}

/**
 * Creates a selector that collects the table-level values a single cell renders with
 * ({@link CellTableModel}) from the {@link TableModel}.
 */
export function createCellTableSelector(
  tableSelector: (store: TableEditableStore) => TableModel
): (store: TableEditableStore) => CellTableModel {
  return createTableEditableStoreStructuredSelector(
    {
      fileId: (store) => store.stableProps.fileId,
      tableNamespace: (store) => store.stableProps.tableNamespace,
      // `props` holds TableEditable's raw props, so this default repeats the one
      // `TableEditable`'s `propsWithDefaults` applies before rendering the class component.
      selectorsEnabled: (store) => store.stableProps.selectorsEnabled ?? true,
      isProtected: (store) => store.stableProps.isProtected ?? false,
      headerRowHeight: (store) => tableSelector(store).state.rowHeights[0],
      openManageSelectorsModal: (store) => store.stableProps.openManageSelectorsModal,
    },
    createDebuggableSelector.withTypes<TableEditableStore>()
  );
}
